
import {defineComponent, PropType} from "vue";
import {BackgroundImageOverlayRestDto} from "@/api/pharma-cms-content-page/models";
import {CodeDescriptionRestDto} from "@/api/pharma-cms-content-block/models";
import {OverlayPositionTypeEnum} from "@/api/enums/overlay-position-type-enum";
import {BackgroundImageOverlayRestDtoModel} from "@/models/api/pharma-cms-content-page/BackgroundImageOverlayRestDtoModel";
import {PaddingRestDtoModel} from "@/models/api/pharma-cms-content-page/PaddingRestDtoModel";
import {ComponentMixin} from "@/components/base/ComponentMixin";
import {EnumRestService} from "@/services/rest/enum/EnumRestService";
import {EnumClassNameCmsEnum} from "@/models/enum/EnumClassNameCmsEnum";
import {DpModuleEnum} from "@/api/enums/dp-module-enum";
import GenericComponentWrapper from "@/components/base/GenericComponentWrapper.vue";
import AdvancedColorEditor from "@/components/layouts/content-pages/wysiwyg/misc/AdvancedColorEditor.vue";
import CssDimensionEditor from "@/components/layouts/content-pages/wysiwyg/misc/CssDimensionEditor.vue";
import InputSelect from "@/components/UI/InputSelect.vue";
import InputTextEditor from "@/components/UI/InputTextEditor.vue";
import Subtitle from "@/components/UI/Subtitle.vue";

export default defineComponent({
  name: "BackgroundImageOverlayDetail",
  mixins: [ComponentMixin],
  components: {
    GenericComponentWrapper,
    AdvancedColorEditor,
    CssDimensionEditor,
    InputSelect,
    InputTextEditor,
    Subtitle,
  },
  emits: ['update:modelValue'],
  props: {
    modelValue: {
      type: Object as PropType<BackgroundImageOverlayRestDto>,
      required: true,
    },
    name: {
      type: String,
      required: true,
    },
    errors: {
      type: Object,
      required: true,
    },
  },
  mounted() {
    this.initialize();
  },
  data() {
    return {
      localDto: BackgroundImageOverlayRestDtoModel.createWithDefaults() as BackgroundImageOverlayRestDto,
      // Always present so the inputs can bind to it; localDto.padding stays null
      // until a side is filled, so an untouched group is not sent.
      paddingDraft: this.modelValue.padding
        ? PaddingRestDtoModel.cloneFrom(this.modelValue.padding)
        : PaddingRestDtoModel.createWithDefaults(),
      enumBlendModeCode: [] as CodeDescriptionRestDto[],
      enumPositionTypeCode: [] as CodeDescriptionRestDto[],
      enumVerticalPositionCode: [] as CodeDescriptionRestDto[],
    };
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(newValue: BackgroundImageOverlayRestDto) {
        this.localDto = BackgroundImageOverlayRestDtoModel.cloneFrom(newValue);
        this.paddingDraft = newValue.padding
          ? PaddingRestDtoModel.cloneFrom(newValue.padding)
          : PaddingRestDtoModel.createWithDefaults();
      },
    },
  },
  computed: {
    showInsets(): boolean {
      return this.localDto.position.position_type_code !== OverlayPositionTypeEnum.STATIC;
    },
  },
  methods: {
    async doInitialize(): Promise<void> {
      [
        this.enumBlendModeCode,
        this.enumVerticalPositionCode,
      ] = await Promise.all([
        EnumRestService.getInstance().getEnumValuesByClassName(EnumClassNameCmsEnum.OVERLAY_BLEND_MODE, DpModuleEnum.CMS),
        EnumRestService.getInstance().getEnumValuesByClassName(EnumClassNameCmsEnum.OBJECT_VERTICAL_POSITION, DpModuleEnum.CMS),
      ]);
      this.enumPositionTypeCode = [
        {code: OverlayPositionTypeEnum.STATIC, description: 'static'},
        {code: OverlayPositionTypeEnum.ABSOLUTE, description: 'absolute'},
        {code: OverlayPositionTypeEnum.RELATIVE, description: 'relative'},
        {code: OverlayPositionTypeEnum.FIXED, description: 'fixed'},
        {code: OverlayPositionTypeEnum.STICKY, description: 'sticky'},
      ];
    },
    onPaddingChanged(): void {
      const draft = this.paddingDraft;
      const hasAnySide = !!(draft.top || draft.right || draft.bottom || draft.left);

      this.localDto.padding = hasAnySide ? draft : null;

      this.emitModelChanged();
    },
    onPositionTypeChanged(): void {
      if (this.localDto.position.position_type_code === OverlayPositionTypeEnum.STATIC) {
        this.localDto.position.top = null;
        this.localDto.position.right = null;
        this.localDto.position.bottom = null;
        this.localDto.position.left = null;
      }
      this.emitModelChanged();
    },
    emitModelChanged(): void {
      this.$emit('update:modelValue', this.localDto);
    },
  },
});
